import { LifeglovHand, LifeglovSize } from '@bioliberty/core';
import { useForm } from '@tanstack/react-form';
import { Plus, Trash } from 'lucide-react';
import { ChangeEvent, PropsWithChildren, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import z from 'zod';
import { useCreateLifeglovLinerOrderMutation } from '../../api/lifeglov-liner-order';
import { useAuthentication } from '../../auth';
import { Button } from '../../components/Button';
import { Dialog, DialogClose, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/Dialog';
import { NumberInput } from '../../components/Input/NumberInput';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/Input/Select';
import { ErrorMessage, InputLabel } from '../../components/Input/TextInput';
import { useActiveSubtenants } from '../../hooks/useActiveSubtenants';
import { useToaster } from '../../hooks/useToaster';

const VALID_HANDS = new Set<string>(Object.values(LifeglovHand));
const VALID_SIZES = new Set<string>(Object.values(LifeglovSize));
const MAX_LINE_ITEMS = 6;
const EMPTY_LINE_ITEM = { hand: '', size: '', count: 5 };

const lineItemSchema = z.object({
  hand: z.enum(LifeglovHand, { error: 'Hand is required' }),
  size: z.enum(LifeglovSize, { error: 'Size is required' }),
  count: z
    .int()
    .min(5, 'Minimum order quantity is 5')
    .refine((n) => n % 5 === 0, 'Quantity must be a multiple of 5')
});

const formSchema = z.object({
  subtenantName: z.string().min(1, 'Clinic is required'),
  items: z
    .array(lineItemSchema)
    .min(1)
    .superRefine((items, ctx) => {
      const seen = new Set<string>();
      for (let i = 0; i < items.length; i++) {
        const key = `${items[i].hand}-${items[i].size}`;
        if (seen.has(key)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Duplicate hand/size combination',
            path: [i, 'hand']
          });
        }
        seen.add(key);
      }
    })
});

interface FormValues {
  subtenantName: string;
  items: { hand: string; size: string; count: number }[];
}

function parseItemsFromSearchParams(searchParams: URLSearchParams): FormValues['items'] {
  const hands = searchParams.getAll('hand');
  const sizes = searchParams.getAll('size');
  const counts = searchParams.getAll('count');
  const length = Math.min(Math.max(hands.length, sizes.length, counts.length), MAX_LINE_ITEMS);
  if (length === 0) return [];

  const items: FormValues['items'] = [];
  for (let i = 0; i < length; i++) {
    const hand = hands[i]?.toUpperCase() ?? '';
    const size = sizes[i]?.toUpperCase() ?? '';
    const parsedCount = Number(counts[i]);
    items.push({
      hand: VALID_HANDS.has(hand) ? hand : '',
      size: VALID_SIZES.has(size) ? size : '',
      count: Number.isInteger(parsedCount) && parsedCount >= 5 ? parsedCount : 5
    });
  }
  return items;
}

interface OrderLifeglovLinerStockDialogProps extends PropsWithChildren {
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function OrderLifeglovLinerStockDialog({
  children,
  defaultOpen = false,
  onOpenChange
}: OrderLifeglovLinerStockDialogProps) {
  const currentUser = useAuthentication();
  const activeSubtenants = useActiveSubtenants();
  const [searchParams] = useSearchParams();
  const [open, setOpen] = useState(defaultOpen);
  const toast = useToaster();
  const [createLifeglovLinerOrder] = useCreateLifeglovLinerOrderMutation();

  const initialItems = useMemo(() => {
    const parsed = parseItemsFromSearchParams(searchParams);
    return parsed.length > 0 ? parsed : [{ ...EMPTY_LINE_ITEM }];
  }, [searchParams]);

  const clinicOptions = useMemo(
    () => activeSubtenants?.map(({ name, label }) => ({ value: name, label })) ?? [],
    [activeSubtenants]
  );

  const defaultValues: FormValues = {
    subtenantName: searchParams.get('clinic') ?? '',
    items: initialItems
  };

  const form = useForm({
    defaultValues,
    validators: {
      onSubmit: formSchema
    },
    onSubmit: async ({ value }) => {
      const subtenant = currentUser.subtenants?.find((s) => s.name === value.subtenantName);
      if (!subtenant) {
        toast.error('Selected clinic is invalid. Please select a valid clinic.');
        return;
      }

      try {
        await createLifeglovLinerOrder({
          subtenantId: subtenant.id,
          items: value.items.map((item) => ({
            hand: item.hand as LifeglovHand,
            size: item.size as LifeglovSize,
            count: item.count
          }))
        }).unwrap();
        toast.success('Refill requested successfully. Check your email for confirmation.');
        setOpen(false);
      } catch {
        toast.error('Failed to request refill. Please try again.');
      }
    }
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        form.reset();
        setOpen(nextOpen);
        onOpenChange?.(nextOpen);
      }}
    >
      {children}

      <DialogContent>
        <DialogHeader>
          <DialogTitle>Request Liner Refill</DialogTitle>
        </DialogHeader>

        <form className="flex flex-col gap-4">
          <form.Field name="subtenantName">
            {(field) => (
              <div>
                <InputLabel required>Clinic</InputLabel>
                <Select onValueChange={(value) => field.handleChange(value)} value={field.state.value}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {clinicOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {field.state.meta.errors.length > 0 && (
                  <ErrorMessage>{field.state.meta.errors[0]?.message}</ErrorMessage>
                )}
              </div>
            )}
          </form.Field>

          <form.Field name="items" mode="array">
            {(itemsField) => (
              <div className="flex flex-col gap-4">
                <div>
                  <InputLabel required>Liners</InputLabel>
                  <hr className="text-highlight w-full" />
                </div>

                {itemsField.state.value.map((_, index) => (
                  <div key={index} className="flex flex-col gap-1">
                    <div className="flex gap-2">
                      <form.Field name={`items[${index}].hand`}>
                        {(field) => (
                          <div className="flex-1">
                            <InputLabel required>Hand</InputLabel>
                            <Select onValueChange={(value) => field.handleChange(value)} value={field.state.value}>
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Hand" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value={LifeglovHand.RIGHT}>Right</SelectItem>
                                <SelectItem value={LifeglovHand.LEFT}>Left</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </form.Field>

                      <form.Field name={`items[${index}].size`}>
                        {(field) => (
                          <div className="flex-1">
                            <InputLabel required>Size</InputLabel>
                            <Select onValueChange={(value) => field.handleChange(value)} value={field.state.value}>
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Size" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value={LifeglovSize.SMALL}>Small</SelectItem>
                                <SelectItem value={LifeglovSize.MEDIUM}>Medium</SelectItem>
                                <SelectItem value={LifeglovSize.LARGE}>Large</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </form.Field>

                      <form.Field name={`items[${index}].count`}>
                        {(field) => (
                          <div className="w-24">
                            <NumberInput
                              label="Quantity"
                              required
                              name={`count-${index}`}
                              value={field.state.value}
                              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                const value = e.target.valueAsNumber;
                                if (!isNaN(value)) {
                                  field.handleChange(value);
                                }
                              }}
                              step={5}
                              min={5}
                            />
                          </div>
                        )}
                      </form.Field>

                      <Button
                        type="button"
                        variant="transparent"
                        size="sm"
                        icon={Trash}
                        iconSize="sm"
                        onClick={() => itemsField.removeValue(index)}
                        disabled={itemsField.state.value.length === 1}
                        className="mt-6"
                        aria-label="Remove liner"
                      />
                    </div>

                    <form.Subscribe
                      selector={(state) => {
                        const errors: string[] = [];
                        for (const key of ['hand', 'size', 'count'] as const) {
                          const fieldErrors = state.fieldMeta[`items[${index}].${key}`]?.errors;
                          if (fieldErrors?.length) {
                            errors.push(`${fieldErrors[0]?.message ?? fieldErrors[0]}`);
                          }
                        }
                        return errors;
                      }}
                    >
                      {(errors) =>
                        errors.length > 0 && (
                          <div className="flex flex-col gap-1">
                            {errors.map((error, i) => (
                              <ErrorMessage key={i}>{error}</ErrorMessage>
                            ))}
                          </div>
                        )
                      }
                    </form.Subscribe>
                  </div>
                ))}

                <Button
                  type="button"
                  variant="transparent"
                  icon={Plus}
                  size="sm"
                  onClick={() => itemsField.pushValue({ ...EMPTY_LINE_ITEM })}
                  disabled={itemsField.state.value.length >= MAX_LINE_ITEMS}
                  className="self-end"
                >
                  Add Liner
                </Button>
              </div>
            )}
          </form.Field>
        </form>

        <DialogFooter>
          <form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting]}>
            {([canSubmit, isSubmitting]) => (
              <Button
                variant="accent"
                onClick={form.handleSubmit}
                disabled={isSubmitting || !canSubmit}
                isLoading={isSubmitting}
              >
                Request
              </Button>
            )}
          </form.Subscribe>

          <DialogClose asChild>
            <Button variant="transparent">Cancel</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
